import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import { axiosKK } from '../../services/networkRequest';
import {
  LogisticsDriverOption,
  LogisticsDriversResponse,
  SplitOrderRequest,
  SplitOrderSegment,
  SubOrderDTO,
  SubOrdersResponse,
} from '../../models/APIModels';
import errorMessage from '../../services/errorMessage';
import SubmitButton from '../SubmitButton';
import SmallButton from '../SmallButton';
import Label from '../Label';

interface Props {
  show: boolean;
  orderId: number;
  close: () => void;
  onSuccess?: () => void;
}

interface SegmentDraft {
  bids: string;
  driverId: string;
}

export default function SplitOrderModal({ show, orderId, close, onSuccess }: Props) {
  const [segments, setSegments] = useState<SegmentDraft[]>([
    { bids: '', driverId: '' },
    { bids: '', driverId: '' },
  ]);
  const [drivers, setDrivers] = useState<LogisticsDriverOption[]>([]);
  const [existingSubOrders, setExistingSubOrders] = useState<SubOrderDTO[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [netError, setNetError] = useState<string>();

  useEffect(() => {
    if (!show) return;
    setNetError(undefined);
    setLoading(true);
    Promise.all([
      axiosKK.get<LogisticsDriversResponse>('/logistics/dashboard/partner/drivers'),
      axiosKK
        .get<SubOrdersResponse>(`/logistics/orders/${orderId}/sub-orders`)
        .catch(() => ({ data: { subOrders: [] } as SubOrdersResponse })),
    ])
      .then(([d, s]) => {
        setDrivers(d.data.drivers || []);
        setExistingSubOrders(s.data.subOrders || []);
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setLoading(false));
  }, [show, orderId]);

  useEffect(() => {
    if (!show) return;
    const esc = (e: KeyboardEvent) => e.key === 'Escape' && close();
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [show, close]);

  if (!show) return null;

  const update = (i: number, patch: Partial<SegmentDraft>) => {
    setSegments((prev) => prev.map((s, idx) => (idx === i ? { ...s, ...patch } : s)));
  };

  const addSegment = () => setSegments((prev) => [...prev, { bids: '', driverId: '' }]);
  const removeSegment = (i: number) =>
    setSegments((prev) => prev.filter((_, idx) => idx !== i));

  const submit = () => {
    const payload: SplitOrderRequest = {
      splits: segments
        .map<SplitOrderSegment | null>((s) => {
          const bids = s.bids
            .split(/[\s,]+/)
            .map((b) => b.trim())
            .filter(Boolean);
          if (bids.length === 0) return null;
          const seg: SplitOrderSegment = { bids };
          if (s.driverId) seg.driverId = Number(s.driverId);
          return seg;
        })
        .filter((s): s is SplitOrderSegment => s !== null),
    };

    if (payload.splits.length < 2) {
      setNetError('A split needs at least two non-empty groups.');
      return;
    }

    setSubmitting(true);
    setNetError(undefined);
    axiosKK
      .post(`/logistics/orders/${orderId}/split`, payload)
      .then(() => {
        onSuccess?.();
        close();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  const unsplit = () => {
    setSubmitting(true);
    setNetError(undefined);
    axiosKK
      .post(`/logistics/orders/${orderId}/unsplit`)
      .then(() => {
        onSuccess?.();
        close();
      })
      .catch((err) => errorMessage(setNetError)(err))
      .finally(() => setSubmitting(false));
  };

  return ReactDOM.createPortal(
    <div className="logistics-modal__overlay" onClick={close}>
      <div
        className="logistics-modal__container logistics-modal__container--wide"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="logistics-modal__form">
          <div className="logistics-modal__header">
            <div className="logistics-modal__header-info">
              <h3>Split Order</h3>
              <span className="logistics-modal__subtitle">Order #{orderId}</span>
            </div>
            <button type="button" className="logistics-modal__close" onClick={close}>
              &times;
            </button>
          </div>
          <div className="logistics-modal__body">
            {loading ? (
              <div className="logistics-modal__loading">Loading…</div>
            ) : (
              <>
                {existingSubOrders.length > 0 && (
                  <div className="logistics-modal__section">
                    <h5 className="logistics-modal__section-title">Existing Sub-orders</h5>
                    <ul>
                      {existingSubOrders.map((s) => (
                        <li key={s.id}>
                          {s.displayLabel} (id {s.id})
                        </li>
                      ))}
                    </ul>
                    <SmallButton onClick={unsplit}>Unsplit</SmallButton>
                  </div>
                )}
                <div className="logistics-modal__section">
                  <h5 className="logistics-modal__section-title">Split into groups</h5>
                  <p className="pricing-hint">
                    Enter one or more BIDs per group (comma or whitespace separated). Each group becomes a sub-order (C1, C2…).
                  </p>
                  {segments.map((seg, i) => (
                    <div key={i} className="split-segment">
                      <div className="logistics-modal__field">
                        <Label htmlFor={`split-bids-${i}`}>Group {i + 1} · BIDs</Label>
                        <textarea
                          id={`split-bids-${i}`}
                          className="logistics-modal__input"
                          rows={2}
                          value={seg.bids}
                          onChange={(e) => update(i, { bids: e.target.value })}
                          placeholder="BID1, BID2, BID3"
                        />
                      </div>
                      <div className="logistics-modal__field">
                        <Label htmlFor={`split-driver-${i}`}>Driver (optional)</Label>
                        <select
                          id={`split-driver-${i}`}
                          className="logistics-modal__select"
                          value={seg.driverId}
                          onChange={(e) => update(i, { driverId: e.target.value })}
                        >
                          <option value="">— Not assigned —</option>
                          {drivers.map((d) => (
                            <option key={d.id} value={d.id}>{d.name}</option>
                          ))}
                        </select>
                      </div>
                      {segments.length > 2 && (
                        <button
                          type="button"
                          className="pricing-table__remove"
                          onClick={() => removeSegment(i)}
                        >
                          Remove group
                        </button>
                      )}
                    </div>
                  ))}
                  <button type="button" className="pricing-add-fee" onClick={addSegment}>
                    + Add Group
                  </button>
                </div>
                {netError && <span className="error">{netError}</span>}
              </>
            )}
          </div>
          <div className="logistics-modal__footer">
            <SubmitButton onClick={submit} isSubmitting={submitting} disabled={submitting || loading}>
              Split
            </SubmitButton>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
